import React, { useState, useEffect } from "react";
import { Modal, DatePicker, Input, Button, Divider } from "antd";
import { useDispatch } from "react-redux";
import { UpdateTimeslot } from "../../Redux/Actions/agentActions";

const { TextArea } = Input;

const DisableTimeslotModal = ({ visible, record, onClose, onSuccess }) => {
  const dispatch = useDispatch();
  const [action, setAction] = useState("disable");
  const [date, setDate] = useState(null);
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!visible) {
      setAction("disable");
      setDate(null);
      setReason("");
    }
  }, [visible]);

  const handleSubmit = async () => {
    let payload = { id: record?.id, action };

    if (action === "disable") {
      if (!date) return;
      payload.date = date.format("YYYY-MM-DD");
      payload.reason = reason || null;
      payload.disabled_by = "admin";
    } else if (action === "enable") {
      if (!date) return;
      payload.date = date.format("YYYY-MM-DD");
    } else if (action === "toggle") {
      payload = { id: record?.id, available: !record?.available };
    }

    setLoading(true);
    try {
      await dispatch(UpdateTimeslot(payload));
      onSuccess?.();
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const isDateRequired = action === "disable" || action === "enable";
  const isSubmitDisabled = isDateRequired && !date;

  const actionColors = {
    disable: "#ff4d4f",
    enable: "#52c41a",
    toggle: "#1677ff",
  };

  const actionLabels = {
    disable: "Disable Slot",
    enable: "Enable Slot",
    toggle: record?.available ? "Mark Unavailable" : "Mark Available",
  };

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      width={480}
      centered
      closable={false}
      destroyOnClose
      styles={{ body: { padding: "0" } }}
      title={null}
    >
      {/* Header */}
      <div
        style={{
          background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 60%, #0f3460 100%)",
          borderRadius: "8px 8px 0 0",
          padding: "24px 28px 20px",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: -30,
            right: -30,
            width: 120,
            height: 120,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.04)",
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: -20,
            left: 60,
            width: 80,
            height: 80,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.03)",
          }}
        />
        <p style={{ color: "rgba(255,255,255,0.5)", margin: "0 0 2px", fontSize: 12, letterSpacing: 1, textTransform: "uppercase" }}>
          Manage Time Slot
        </p>
        <h2 style={{ color: "#fff", margin: 0, fontSize: 20, fontWeight: 700, letterSpacing: 0.3 }}>
          Update Slot
        </h2>
        {record && (
          <div style={{ marginTop: 12, display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(255,255,255,0.08)", borderRadius: 20, padding: "4px 14px" }}>
            <span style={{ fontSize: 16 }}>🕐</span>
            <span style={{ color: "#e0e0e0", fontWeight: 600, fontSize: 14 }}>{record.time}</span>
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: record.available ? "#52c41a" : "#ff4d4f",
                display: "inline-block",
              }}
            />
            <span style={{ color: "rgba(255,255,255,0.5)", fontSize: 12 }}>
              {record.available ? "Available" : "Unavailable"}
            </span>
          </div>
        )}
      </div>

      {/* Body */}
      <div style={{ padding: "24px 28px 28px" }}>
        {/* Action Selector */}
        <div style={{ marginBottom: 20 }}>
          <label style={{ display: "block", marginBottom: 10, fontWeight: 600, color: "#374151", fontSize: 13 }}>
            Select Action
          </label>
          <div style={{ display: "flex", gap: 10 }}>
            {["disable", "enable", "toggle"].map((act) => {
              const colors = { disable: { bg: "#fff1f0", border: "#ff4d4f", text: "#ff4d4f" }, enable: { bg: "#f6ffed", border: "#52c41a", text: "#52c41a" }, toggle: { bg: "#e6f4ff", border: "#1677ff", text: "#1677ff" } };
              const isActive = action === act;
              return (
                <button
                  key={act}
                  onClick={() => { setAction(act); setDate(null); setReason(""); }}
                  style={{
                    flex: 1,
                    padding: "10px 4px",
                    border: `1.5px solid ${isActive ? colors[act].border : "#e5e7eb"}`,
                    borderRadius: 8,
                    background: isActive ? colors[act].bg : "#fafafa",
                    color: isActive ? colors[act].text : "#9ca3af",
                    fontWeight: isActive ? 700 : 500,
                    fontSize: 12,
                    cursor: "pointer",
                    transition: "all 0.2s",
                    textTransform: "capitalize",
                    letterSpacing: 0.3,
                  }}
                >
                  {act === "disable" ? "🚫 Disable" : act === "enable" ? "✅ Enable" : "🔄 Toggle"}
                </button>
              );
            })}
          </div>
        </div>

        <Divider style={{ margin: "0 0 20px", borderColor: "#f0f0f0" }} />

        {/* Date Picker — shown for disable/enable */}
        {isDateRequired && (
          <div style={{ marginBottom: 18 }}>
            <label style={{ display: "block", marginBottom: 8, fontWeight: 600, color: "#374151", fontSize: 13 }}>
              Date <span style={{ color: "#ff4d4f" }}>*</span>
            </label>
            <DatePicker
              style={{ width: "100%", height: 40, borderRadius: 8 }}
              value={date}
              onChange={(val) => setDate(val)}
              disabledDate={(current) => current && current.valueOf() < Date.now() - 86400000}
              placeholder="Select date"
            />
          </div>
        )}

        {/* Reason — shown only for disable */}
        {action === "disable" && (
          <div style={{ marginBottom: 18 }}>
            <label style={{ display: "block", marginBottom: 8, fontWeight: 600, color: "#374151", fontSize: 13 }}>
              Reason <span style={{ color: "#9ca3af", fontWeight: 400 }}>(optional)</span>
            </label>
            <TextArea
              rows={3}
              placeholder="e.g. Maintenance, Holiday, Staff leave..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              style={{ borderRadius: 8, resize: "none" }}
            />
          </div>
        )}

        {/* Toggle info */}
        {action === "toggle" && (
          <div
            style={{
              background: "#f0f9ff",
              border: "1px solid #bae6fd",
              borderRadius: 8,
              padding: "12px 16px",
              marginBottom: 18,
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <span style={{ fontSize: 20 }}>ℹ️</span>
            <span style={{ fontSize: 13, color: "#0369a1", lineHeight: 1.5 }}>
              This will mark the slot as <strong>{record?.available ? "Unavailable" : "Available"}</strong> globally (no date restriction).
            </span>
          </div>
        )}

        {/* Footer Buttons */}
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 4 }}>
          <Button
            onClick={onClose}
            style={{ borderRadius: 8, height: 40, paddingInline: 20, fontWeight: 500 }}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            loading={loading}
            disabled={isSubmitDisabled}
            onClick={handleSubmit}
            style={{
              borderRadius: 8,
              height: 40,
              paddingInline: 24,
              fontWeight: 600,
              background: isSubmitDisabled ? undefined : actionColors[action],
              borderColor: isSubmitDisabled ? undefined : actionColors[action],
            }}
          >
            {actionLabels[action]}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default DisableTimeslotModal;